/**
 * BookingsListDialog (DNKADM-0300 E/F) — ONE bookings-list card, never forked:
 * the coach popout (a coach-load bar tap), the bay popout (a walk-in row tap),
 * and the after-hours list (the control for bookings outside the operating
 * window) all render through it. A real modal dialog on every pointer —
 * click/tap on desktop AND tablet, never a hover card (a hover card holding
 * click targets forces the pointer across dead space). Each row is a ≥48px
 * button that opens the booking's detail surface via `onSelect`.
 */
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { resolveCalendarMaps } from '@/theme/maps/calendar';
import { scrubBrand } from '@/utils/whitelabel';
import { clockInZone } from '@/utils/clockTime';
import type { Reservation } from '@/api/reservations';
import { reservationStatusLabel } from '@/domain/v2Enums';
import { deriveStatusBucket } from './derive';

export interface BookingsListDialogProps {
  open: boolean;
  /** The card's subject, e.g. `Jordan — today` or `After hours`. */
  title: string;
  /** Optional one-line context under the title. */
  subtitle?: string;
  reservations: Reservation[];
  timeZone: string | null;
  onClose: () => void;
  /** Open the detail surface for a row (the caller also closes this dialog). */
  onSelect: (reservation: Reservation) => void;
}

/**
 * A short venue-clock time label for an ISO instant — 12-hour through the shared
 * formatter (DNKADM-0386 U2); the zone rule is unchanged (the venue zone, else device).
 */
function timeLabel(iso: string | null, timeZone: string | null): string {
  return clockInZone(iso, timeZone);
}

/**
 * The row's status word + pill pair (the calendar's shared vocabulary). The
 * attendance outcomes keep their bucket's pill colour but carry their own word
 * from the shared `reservationStatusLabel` (the drawer header's source):
 * `no_show` buckets as cancelled and `showed` as confirmed, so without this a
 * no-show read "Cancelled" and a checked-in booking read "Confirmed".
 */
function statusOf(reservation: Reservation, cm: ReturnType<typeof resolveCalendarMaps>) {
  if (reservation.status === 'no_show') {
    return { label: reservationStatusLabel('no_show'), pill: cm.statusPill.cancelled };
  }
  if (reservation.status === 'showed') {
    return { label: reservationStatusLabel('showed'), pill: cm.statusPill.confirmed };
  }
  const bucket = deriveStatusBucket(reservation.status);
  if (bucket === 'cancelled') return { label: 'Cancelled', pill: cm.statusPill.cancelled };
  if (bucket === 'pending') return { label: 'Pending', pill: cm.statusPill.pending };
  if (bucket === 'confirmed') return { label: 'Confirmed', pill: cm.statusPill.confirmed };
  return { label: reservation.status ?? '—', pill: cm.statusPill.neutral };
}

export function BookingsListDialog({
  open,
  title,
  subtitle,
  reservations,
  timeZone,
  onClose,
  onSelect,
}: BookingsListDialogProps) {
  const cm = resolveCalendarMaps(useTheme());
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 600 }}>{scrubBrand(title)}</Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12.5, color: cm.textMuted, mt: 0.25 }}>
              {scrubBrand(subtitle)}
            </Typography>
          )}
        </Box>
        <IconAction size="small" onClick={onClose} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {reservations.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: cm.textMuted, py: 1.5 }}>
            Nothing booked.
          </Typography>
        ) : (
          <Stack sx={{ gap: 0.75 }}>
            {reservations.map((r) => {
              const status = statusOf(r, cm);
              return (
                <Box
                  key={r.reservationUuid ?? `${r.startTime}-${r.contactName}`}
                  component="button"
                  type="button"
                  onClick={() => onSelect(r)}
                  aria-label={`Open ${r.contactName ?? r.serviceName ?? 'reservation'} ${timeLabel(r.startTime, timeZone)}`}
                  sx={{
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    minHeight: 52,
                    px: 1.5,
                    py: 1,
                    border: `1px solid ${cm.rowBorder}`,
                    borderRadius: '10px',
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: cm.rowHoverBg },
                    '&:active': { bgcolor: cm.rowHoverBg },
                  }}
                >
                  <Typography variant="mono" sx={{ fontSize: 12.5, color: cm.textMeta, width: 76, flexShrink: 0 }}>
                    {timeLabel(r.startTime, timeZone)}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: cm.textPrimary }} noWrap>
                      {scrubBrand(r.contactName ?? 'Reservation')}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: cm.textMuted }} noWrap>
                      {/* service · resource · staff — from the v2 canonical
                          fields, NOT the `staff` alias: that alias is
                          deliberately synthesized from the RESOURCE when no
                          staff is assigned (the calendar bay axis), which
                          printed the bay twice ("Bay 2 · Bay 2") on staffless
                          bookings (DNKADM-0303 U7). Absent staff drops the
                          segment entirely. */}
                      {scrubBrand(
                        [r.serviceName, r.assignedResourceName, r.assignedStaffName]
                          .filter(Boolean)
                          .join(' · '),
                      )}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      px: '8px',
                      py: '3px',
                      borderRadius: '999px',
                      color: status.pill.fg,
                      bgcolor: status.pill.bg,
                      flexShrink: 0,
                    }}
                  >
                    {status.label}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
